import { LocationSearchCoords } from '../LocationSearch.types';

export type SiteWithCoords = {
  latitude?: number;
  longitude?: number;
};

export interface MapBoundsLiteral {
  north: number;
  south: number;
  east: number;
  west: number;
}

export const METERS_PER_MILE = 1609.34;
export const EARTH_RADIUS_M = 6378137;
export const MAP_FIT_BOUNDS_PADDING_PX = 48;
/** Minimum radius when fitting a single site pin so fitBounds does not max-zoom. */
export const SINGLE_SITE_FIT_RADIUS_MILES = 10;

/** Contiguous USA viewport for the "Any distance" filter. */
export const USA_BOUNDS: MapBoundsLiteral = {
  north: 49.4,
  south: 24.5,
  west: -125.0,
  east: -66.9,
};

type MapWithFitBounds = {
  fitBounds: (bounds: MapBoundsLiteral, padding?: number) => void;
};

export function getRadiusBoundsLiteral(
  center: LocationSearchCoords,
  miles: number
): MapBoundsLiteral {
  const radiusMeters = miles * METERS_PER_MILE;
  const latDelta = (radiusMeters / EARTH_RADIUS_M) * (180 / Math.PI);
  const lngDelta = latDelta / Math.cos((center.lat * Math.PI) / 180);

  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}

function hasValidSiteCoords(site: SiteWithCoords): site is SiteWithCoords & {
  latitude: number;
  longitude: number;
} {
  return (
    typeof site.latitude === 'number' &&
    typeof site.longitude === 'number' &&
    !Number.isNaN(site.latitude) &&
    !Number.isNaN(site.longitude)
  );
}

export function getSitesBoundsLiteral(sites: SiteWithCoords[]): MapBoundsLiteral | null {
  const sitesWithCoords = sites.filter(hasValidSiteCoords);

  if (sitesWithCoords.length === 0) {
    return null;
  }

  if (sitesWithCoords.length === 1) {
    const site = sitesWithCoords[0];
    return getRadiusBoundsLiteral(
      { lat: site.latitude, lng: site.longitude },
      SINGLE_SITE_FIT_RADIUS_MILES
    );
  }

  let north = sitesWithCoords[0].latitude;
  let south = sitesWithCoords[0].latitude;
  let east = sitesWithCoords[0].longitude;
  let west = sitesWithCoords[0].longitude;

  sitesWithCoords.forEach((site) => {
    north = Math.max(north, site.latitude);
    south = Math.min(south, site.latitude);
    east = Math.max(east, site.longitude);
    west = Math.min(west, site.longitude);
  });

  return { north, south, east, west };
}

export function resolveLocationSearchMapBounds({
  coords,
  distanceMiles,
  hasLocationContext = false,
  sites = [],
}: {
  coords?: LocationSearchCoords | null;
  distanceMiles?: number;
  hasLocationContext?: boolean;
  sites?: SiteWithCoords[];
}): MapBoundsLiteral | null {
  if (!hasLocationContext) {
    return getSitesBoundsLiteral(sites);
  }

  if (distanceMiles == null || !Number.isFinite(distanceMiles) || distanceMiles <= 0 || !coords) {
    return USA_BOUNDS;
  }

  return getRadiusBoundsLiteral(coords, distanceMiles);
}

export function applyLocationSearchMapViewport(
  map: MapWithFitBounds | null,
  bounds: MapBoundsLiteral | null,
  padding: number = MAP_FIT_BOUNDS_PADDING_PX
): void {
  if (!map || !bounds) {
    return;
  }

  map.fitBounds(bounds, padding);
}
